import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import type { StoreProductInput, StoreProductUploadResult, StoreProductUploadRow } from './storeProductUploadApi';
import type { AdminCanonicalProduct } from '../components/admin/productDetail/productDetailTypes';

/**
 * Customer product submissions (flag `product_submissions`, MOT-158). The
 * routes are `requireFlag`-gated on the server; callers render only while the
 * flag is on. A store submits under `/store/product-submissions`, a vendor
 * under `/vendor/product-submissions`; the admin QA list is `/admin/product-submissions`.
 */

export type ProductSubmissionScope = 'store' | 'vendor';
export type ProductSubmissionStatus = 'in_review' | 'needs_answer' | 'approved' | 'promoted' | 'rejected';
export type SubmissionAnswerField = 'name' | 'model' | 'upc' | 'mpn' | 'description' | 'launchDate';

export interface ProductSubmissionQuestion {
  id: string;
  question: string;
  field?: SubmissionAnswerField | null;
  askedAt: string;
  answer?: string | null;
  answeredAt?: string | null;
}

export interface ProductSubmission {
  id: string;
  provisionalProductId: string;
  brand: string;
  model: string;
  name: string;
  sku: string | null;
  upc: string | null;
  category: string | null;
  description: string;
  imageUrls: string[];
  msrp: number | null;
  status: ProductSubmissionStatus;
  submittedAt: string | null;
  promotedCanonicalId: string | null;
  reviewNote: string | null;
  questions: ProductSubmissionQuestion[];
  openQuestions: number;
}

export interface ProductSubmissionAdmin extends ProductSubmission {
  kind: ProductSubmissionScope;
  /** The first submitter; `submitters` lists every organization that submitted the product (MOT-737). */
  submittedByOrganizationId: string;
  submittedByOrganizationName: string | null;
  submitters: Array<{ organizationId: string; organizationName: string | null; kind: ProductSubmissionScope }>;
  stagingStatus: string;
}

export interface ProductSubmissionRow extends StoreProductUploadRow {
  submissionId?: string;
  /** Present only when the submission carried `details`: whether they were saved (MOT-701). */
  detailsApplied?: boolean;
}

export interface ProductSubmissionResult extends StoreProductUploadResult {
  rows: ProductSubmissionRow[];
  submitted: number;
}

/** A submission with its provisional product, the document the shared product editor shows (MOT-695). */
export interface ProductSubmissionProduct {
  submission: ProductSubmission;
  product: AdminCanonicalProduct;
}

const failure = async (response: Response, fallback: string): Promise<never> => {
  let message = fallback;
  try {
    const body = await response.json();
    message = body?.message || body?.error || fallback;
  } catch {
    // keep the fallback
  }
  throw new Error(message);
};

const json = async () => ({ ...(await getAuthHeaders()), 'Content-Type': 'application/json' });

export const productSubmissionsApi = {
  /**
   * One product. `details` is the rest of what the product editor filled in
   * (MOT-701), saved to the submitted product in the same request; a catalog
   * match saves none of it and its row says so (`detailsApplied: false`).
   */
  async submitOne(scope: ProductSubmissionScope, input: StoreProductInput, details?: Record<string, unknown> | null): Promise<ProductSubmissionResult> {
    const body = details ? { ...input, details } : input;
    const response = await makeApiCall(`/${scope}/product-submissions`, { method: 'POST', headers: await json(), body: JSON.stringify(body) });
    if (!response.ok) return failure(response, 'Failed to submit the product');
    return (await response.json()).data;
  },

  async submitCsv(scope: ProductSubmissionScope, file: File): Promise<ProductSubmissionResult> {
    const headers = (await getAuthHeaders()) as Record<string, string>;
    delete headers['Content-Type'];
    const body = new FormData();
    body.append('file', file);
    const response = await makeApiCall(`/${scope}/product-submissions/upload`, { method: 'POST', headers, body });
    if (!response.ok) return failure(response, 'Failed to submit the products');
    return (await response.json()).data;
  },

  async downloadTemplate(scope: ProductSubmissionScope): Promise<void> {
    const response = await makeApiCall(`/${scope}/product-submissions/template`, { headers: await getAuthHeaders() });
    if (!response.ok) return failure(response, 'Failed to download the template');
    const url = URL.createObjectURL(await response.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product-submissions-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  },

  async list(scope: ProductSubmissionScope): Promise<ProductSubmission[]> {
    const response = await makeApiCall(`/${scope}/product-submissions`, { headers: await getAuthHeaders() });
    if (!response.ok) return failure(response, 'Failed to load your submissions');
    return (await response.json()).data || [];
  },

  async answer(scope: ProductSubmissionScope, submissionId: string, questionId: string, answer: string): Promise<ProductSubmission> {
    const response = await makeApiCall(`/${scope}/product-submissions/${encodeURIComponent(submissionId)}/answer`, {
      method: 'POST',
      headers: await json(),
      body: JSON.stringify({ questionId, answer }),
    });
    if (!response.ok) return failure(response, 'Failed to save the answer');
    return (await response.json()).data;
  },

  async product(scope: ProductSubmissionScope, submissionId: string): Promise<ProductSubmissionProduct> {
    const response = await makeApiCall(`/${scope}/product-submissions/${encodeURIComponent(submissionId)}/product`, { headers: await getAuthHeaders() });
    if (!response.ok) return failure(response, 'Failed to load the submitted product');
    return (await response.json()).data;
  },

  /** Saves the editor's changes to the submitted product (the fields `submissionProductEditSchema` allows). */
  async updateProduct(scope: ProductSubmissionScope, submissionId: string, payload: Record<string, unknown>): Promise<ProductSubmissionProduct> {
    const response = await makeApiCall(`/${scope}/product-submissions/${encodeURIComponent(submissionId)}/product`, {
      method: 'PUT',
      headers: await json(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) return failure(response, 'Failed to save the product');
    return (await response.json()).data;
  },

  /** Store only: the store's own provisional products into its sync selection (gated by `product_submissions`, not the upload flag). */
  async select(productIds: string[]): Promise<{ added: string[]; skipped: string[] }> {
    const response = await makeApiCall('/store/product-submissions/select', { method: 'POST', headers: await json(), body: JSON.stringify({ productIds }) });
    if (!response.ok) return failure(response, 'Failed to add products to the sync selection');
    return (await response.json()).data;
  },

  async listAdmin(status?: ProductSubmissionStatus | '', cursor?: string | null): Promise<{ items: ProductSubmissionAdmin[]; nextCursor: string | null }> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (cursor) params.set('cursor', cursor);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await makeApiCall(`/admin/product-submissions${query}`, { headers: await getAuthHeaders() });
    if (!response.ok) return failure(response, 'Failed to load product submissions');
    const body = await response.json();
    return { items: body.data || [], nextCursor: body.nextCursor ?? null };
  },

  async ask(submissionId: string, question: string, field: SubmissionAnswerField | null): Promise<ProductSubmissionAdmin> {
    const response = await makeApiCall(`/admin/product-submissions/${encodeURIComponent(submissionId)}/questions`, {
      method: 'POST',
      headers: await json(),
      body: JSON.stringify({ question, field }),
    });
    if (!response.ok) return failure(response, 'Failed to ask the question');
    return (await response.json()).data;
  },
};
